import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  MenuItem,
  InputAdornment,
  Snackbar,
  Divider,
  Autocomplete,
  Card,
  CardContent,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  ShoppingCart as OrderIcon,
  Receipt as InvoiceIcon,
  Refresh,
  HourglassTop,
  CurrencyRupee,
  CalendarMonth,
} from '@mui/icons-material';
import { formatCurrency, formatIndianNumber } from '../../utils/currency';
import {
  PageHeader,
  StatCard,
  FilterBar,
  FilterSpacer,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  CodeCell,
  StatusDot,
  StatusTone,
  TableEmptyState,
  ErrorState,
} from '../../components/common/pageKit';

interface Customer {
  id: number;
  customer_code: string;
  company_name: string;
  credit_limit: number;
  gstin?: string;
}

interface Product {
  id: number;
  product_code: string;
  product_name: string;
  base_price: number;
  unit_of_measure: string;
}

interface SalesOrderItem {
  id?: number;
  product_id: number;
  product_name?: string;
  quantity: number;
  unit_price: number;
  discount_percent: number;
  tax_amount: number;
  line_total: number;
}

interface SalesOrder {
  id: number;
  order_number: string;
  customer_id: number;
  customer_name?: string;
  order_date: string;
  delivery_date?: string;
  subtotal: number;
  tax_amount: number;
  total_amount: number;
  status: 'PENDING' | 'CONFIRMED' | 'SHIPPED' | 'DELIVERED' | 'CANCELLED';
  created_by: number;
  created_at: string;
  items?: SalesOrderItem[];
}

const STATUS_TONE: Record<SalesOrder['status'], StatusTone> = {
  PENDING: 'warning',
  CONFIRMED: 'info',
  SHIPPED: 'info',
  DELIVERED: 'success',
  CANCELLED: 'error',
};

const fmtDate = (v: string | null | undefined) => (v ? new Date(v).toLocaleDateString('en-IN') : '—');

const SalesOrderPage: React.FC = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<SalesOrder[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [deliveryDate, setDeliveryDate] = useState('');
  const [orderItems, setOrderItems] = useState<SalesOrderItem[]>([]);
  const [newItem, setNewItem] = useState<Partial<SalesOrderItem>>({
    product_id: 0,
    quantity: 1,
    unit_price: 0,
    discount_percent: 0,
  });

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch('/api/v1/sales/orders', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch sales orders');
      }

      const data = await response.json();
      setOrders(data.data.orders);
      setError(null);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to load sales orders');
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCustomers = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/v1/sales/customers?isActive=true', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setCustomers(data.data.customers);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  }, []);

  const fetchProducts = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/v1/products?isActive=true', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setProducts(data.data.products || []);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
    fetchCustomers();
    fetchProducts();
  }, [fetchOrders, fetchCustomers, fetchProducts]);

  const calculateItemTotal = (item: Partial<SalesOrderItem>) => {
    const subtotal = (item.quantity || 0) * (item.unit_price || 0);
    const discountAmount = subtotal * ((item.discount_percent || 0) / 100);
    const discountedAmount = subtotal - discountAmount;
    const taxAmount = discountedAmount * 0.18; // 18% GST
    return {
      subtotal: subtotal,
      discountAmount: discountAmount,
      taxAmount: taxAmount,
      lineTotal: discountedAmount + taxAmount
    };
  };

  const addItemToOrder = () => {
    if (!newItem.product_id || !newItem.quantity || !newItem.unit_price) {
      setSnackbar({ open: true, message: 'Please fill all required fields', severity: 'error' });
      return;
    }

    const selectedProduct = products.find(p => p.id === newItem.product_id);
    const calculations = calculateItemTotal(newItem);

    const orderItem: SalesOrderItem = {
      product_id: newItem.product_id!,
      product_name: selectedProduct?.product_name || '',
      quantity: newItem.quantity!,
      unit_price: newItem.unit_price!,
      discount_percent: newItem.discount_percent || 0,
      tax_amount: calculations.taxAmount,
      line_total: calculations.lineTotal,
    };

    setOrderItems([...orderItems, orderItem]);
    setNewItem({ product_id: 0, quantity: 1, unit_price: 0, discount_percent: 0 });
  };

  const removeItemFromOrder = (index: number) => {
    setOrderItems(orderItems.filter((_, i) => i !== index));
  };

  const calculateOrderTotals = () => {
    const subtotal = orderItems.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
    const totalTax = orderItems.reduce((sum, item) => sum + item.tax_amount, 0);
    const total = orderItems.reduce((sum, item) => sum + item.line_total, 0);

    return { subtotal, totalTax, total };
  };

  const handleCreateOrder = async () => {
    if (!selectedCustomer || orderItems.length === 0) {
      setSnackbar({ open: true, message: 'Please select a customer and add items', severity: 'error' });
      return;
    }

    try {
      const token = localStorage.getItem('token');

      const orderData = {
        customerId: selectedCustomer.id,
        deliveryDate: deliveryDate || null,
        items: orderItems.map(item => ({
          productId: item.product_id,
          quantity: item.quantity,
          unitPrice: item.unit_price,
          discountPercent: item.discount_percent,
        })),
      };

      const response = await fetch('/api/v1/sales/orders', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(orderData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create sales order');
      }

      setSnackbar({ open: true, message: 'Sales order created successfully', severity: 'success' });
      resetForm();
      setOpenDialog(false);
      fetchOrders();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error instanceof Error ? error.message : 'Failed to create sales order',
        severity: 'error'
      });
    }
  };

  const resetForm = () => {
    setSelectedCustomer(null);
    setDeliveryDate('');
    setOrderItems([]);
    setNewItem({ product_id: 0, quantity: 1, unit_price: 0, discount_percent: 0 });
  };

  const openCreateDialog = () => {
    resetForm();
    setOpenDialog(true);
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch = order.order_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (order.customer_name && order.customer_name.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = !filterStatus || order.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const orderTotals = calculateOrderTotals();

  const hasFilters = !!(searchTerm || filterStatus);
  const clearFilters = () => {
    setSearchTerm('');
    setFilterStatus('');
  };

  // Stat cards computed client-side from the loaded list -- null while
  // loading so a slow fetch never briefly reads as "zero orders".
  const totalCount = loading ? null : orders.length;
  const openCount = loading ? null : orders.filter(o => o.status !== 'DELIVERED' && o.status !== 'CANCELLED').length;
  const totalValue = loading ? null : orders.reduce((sum, o) => sum + Number(o.total_amount || 0), 0);
  const thisMonthCount = loading
    ? null
    : orders.filter(o => {
        const d = new Date(o.order_date);
        const now = new Date();
        return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
      }).length;

  return (
    <Box>
      <PageHeader
        title="Sales Orders"
        subtitle="Create and manage sales orders with GST calculations"
        actions={
          <>
            <IconButton onClick={fetchOrders} disabled={loading}>
              <Refresh />
            </IconButton>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={openCreateDialog}
            >
              Create Order
            </Button>
          </>
        }
      />

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 2.5 }}>
        <StatCard title="Total Orders" value={totalCount} icon={<OrderIcon />} color="#2563eb" />
        <StatCard title="Open" value={openCount} icon={<HourglassTop />} color="#d97706" />
        <StatCard
          title="Total Value"
          value={totalValue === null ? null : formatIndianNumber(totalValue)}
          icon={<CurrencyRupee />}
          color="#059669"
        />
        <StatCard title="This Month" value={thisMonthCount} icon={<CalendarMonth />} color="#7c3aed" />
      </Box>

      <FilterBar>
        <TextField
          size="small"
          placeholder="Search orders..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ minWidth: 240 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          select
          size="small"
          label="Status"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          sx={{ minWidth: 190 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FilterIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        >
          <MenuItem value="">All Status</MenuItem>
          <MenuItem value="PENDING">Pending</MenuItem>
          <MenuItem value="CONFIRMED">Confirmed</MenuItem>
          <MenuItem value="SHIPPED">Shipped</MenuItem>
          <MenuItem value="DELIVERED">Delivered</MenuItem>
          <MenuItem value="CANCELLED">Cancelled</MenuItem>
        </TextField>

        <FilterSpacer />

        {hasFilters && (
          <Button size="small" onClick={clearFilters}>
            Clear Filters
          </Button>
        )}
        <Typography variant="body2" color="text.secondary">
          {filteredOrders.length} orders · {formatCurrency(filteredOrders.reduce((sum, order) => sum + order.total_amount, 0))}
        </Typography>
      </FilterBar>

      {/* Orders Table */}
      <TableSurface>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                {['Order Number', 'Customer', 'Order Date', 'Delivery Date', 'Total Amount', 'Status', 'Actions'].map((h) => (
                  <TableCell key={h} sx={headerCellSx}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6, border: 0 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ border: 0 }}>
                    <ErrorState message={error} onRetry={fetchOrders} />
                  </TableCell>
                </TableRow>
              ) : filteredOrders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ border: 0 }}>
                    <TableEmptyState
                      title="No sales orders found"
                      hint={searchTerm || filterStatus ? 'Try adjusting your search criteria' : 'Start by creating your first sales order'}
                    />
                  </TableCell>
                </TableRow>
              ) : (
                filteredOrders.map((order) => (
                  <TableRow
                    key={order.id}
                    sx={{ ...bodyRowSx, cursor: 'pointer' }}
                    hover
                    onClick={() => navigate(`/sales/orders/${order.id}`)}
                  >
                    <TableCell sx={{ py: 1.5 }}>
                      <CodeCell code={order.order_number} tint="#2563eb" icon={<OrderIcon fontSize="small" />} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {order.customer_name || `Customer ${order.customer_id}`}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {fmtDate(order.order_date)}
                    </TableCell>
                    <TableCell>
                      {fmtDate(order.delivery_date)}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {formatCurrency(order.total_amount)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Tax: {formatCurrency(order.tax_amount)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <StatusDot label={order.status} tone={STATUS_TONE[order.status] || 'neutral'} />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }} onClick={(e) => e.stopPropagation()}>
                        <IconButton
                          size="small"
                          color="primary"
                          title="Edit Order"
                          disabled={order.status !== 'PENDING'}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="success"
                          title="Create Invoice"
                          disabled={order.status !== 'CONFIRMED'}
                        >
                          <InvoiceIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TableSurface>

      {/* Create Sales Order Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Create New Sales Order
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {/* Customer Selection */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Autocomplete
                options={customers}
                getOptionLabel={(customer) => `${customer.customer_code} - ${customer.company_name}`}
                value={selectedCustomer}
                onChange={(_, customer) => setSelectedCustomer(customer)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Customer *"
                    required
                  />
                )}
              />
            </Grid>

            {/* Delivery Date */}
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Delivery Date"
                type="date"
                value={deliveryDate}
                onChange={(e) => setDeliveryDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: new Date().toISOString().split('T')[0] }}
              />
            </Grid>

            {/* Customer Info Display */}
            {selectedCustomer && (
              <Grid size={{ xs: 12 }}>
                <Card sx={{ bgcolor: 'info.50', border: '1px solid', borderColor: 'info.200' }}>
                  <CardContent sx={{ py: 2 }}>
                    <Typography variant="subtitle2" color="info.main">
                      Customer Details
                    </Typography>
                    <Typography variant="body2">
                      <strong>Credit Limit:</strong> {formatCurrency(selectedCustomer.credit_limit)} |
                      <strong> GSTIN:</strong> {selectedCustomer.gstin || 'Not provided'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}

            <Grid size={{ xs: 12 }}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Add Items
              </Typography>
            </Grid>

            {/* Add New Item */}
            <Grid size={{ xs: 12, md: 3 }}>
              <Autocomplete
                options={products}
                getOptionLabel={(product) => `${product.product_code} - ${product.product_name}`}
                onChange={(_, product) => {
                  setNewItem(prev => ({
                    ...prev,
                    product_id: product?.id || 0,
                    unit_price: product?.base_price || 0
                  }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Product"
                    size="small"
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 2 }}>
              <TextField
                fullWidth
                label="Quantity"
                type="number"
                size="small"
                value={newItem.quantity}
                onChange={(e) => setNewItem(prev => ({ ...prev, quantity: parseInt(e.target.value) || 0 }))}
                inputProps={{ min: 1 }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 2 }}>
              <TextField
                fullWidth
                label="Unit Price"
                type="number"
                size="small"
                value={newItem.unit_price}
                onChange={(e) => setNewItem(prev => ({ ...prev, unit_price: parseFloat(e.target.value) || 0 }))}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 2 }}>
              <TextField
                fullWidth
                label="Discount %"
                type="number"
                size="small"
                value={newItem.discount_percent}
                onChange={(e) => setNewItem(prev => ({ ...prev, discount_percent: parseFloat(e.target.value) || 0 }))}
                inputProps={{ min: 0, max: 100 }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <Button
                variant="contained"
                onClick={addItemToOrder}
                startIcon={<AddIcon />}
                fullWidth
                disabled={!newItem.product_id || !newItem.quantity || !newItem.unit_price}
              >
                Add Item
              </Button>
            </Grid>

            {/* Order Items Table */}
            {orderItems.length > 0 && (
              <Grid size={{ xs: 12 }}>
                <TableContainer component={Paper} sx={{ mt: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={headerCellSx}>Product</TableCell>
                        <TableCell align="center" sx={headerCellSx}>Qty</TableCell>
                        <TableCell align="right" sx={headerCellSx}>Unit Price</TableCell>
                        <TableCell align="right" sx={headerCellSx}>Discount</TableCell>
                        <TableCell align="right" sx={headerCellSx}>Tax (18%)</TableCell>
                        <TableCell align="right" sx={headerCellSx}>Total</TableCell>
                        <TableCell align="center" sx={headerCellSx}>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {orderItems.map((item, index) => (
                        <TableRow key={index} sx={bodyRowSx}>
                          <TableCell>{item.product_name}</TableCell>
                          <TableCell align="center">{item.quantity}</TableCell>
                          <TableCell align="right">{formatCurrency(item.unit_price)}</TableCell>
                          <TableCell align="right">{item.discount_percent}%</TableCell>
                          <TableCell align="right">{formatCurrency(item.tax_amount)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            {formatCurrency(item.line_total)}
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => removeItemFromOrder(index)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell colSpan={5} align="right" sx={{ fontWeight: 'bold' }}>
                          Order Total:
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.1em' }}>
                          {formatCurrency(orderTotals.total)}
                        </TableCell>
                        <TableCell />
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpenDialog(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateOrder}
            disabled={!selectedCustomer || orderItems.length === 0}
          >
            Create Sales Order
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SalesOrderPage;
